import React, { useState } from 'react';
import { useDashboardTranslation } from '../i18n';
import { X, ArrowLeft, RotateCcw, Check } from 'lucide-react';
import { useQueryClient } from '@tanstack/react-query';
import type {
  WidgetDefinition, UserWidgetPreference, SchemaField,
  FieldMappingOverride, DashboardPeriod,
} from '../types';
import { getCompatibleTypes, extractChartContract } from '../types';
import { resolveChartFields } from '../utils/resolveChartFields';
import { WIDGET_TYPE_MAP } from '../utils/widgetTypeConfig';
import { FieldMappingPanel } from './FieldMappingPanel';

/**
 * Vacío a propósito. Esta era la TERCERA lista de categorías del módulo y —peor— no coincidía con
 * las otras dos: agregaba `'General'`, que no existía en el Widget Builder. Un usuario podía
 * clasificar un widget en una categoría que el asistente de creación no ofrecía.
 *
 * Ahora la única fuente son las `availableCategories` que declara el host.
 */
const DEFAULT_CATEGORIES: string[] = [];

interface WidgetCustomizationPanelProps {
  widget: WidgetDefinition;
  /** Ausente = modo "agregar", presente = modo "personalizar" */
  preference?: UserWidgetPreference;
  /** Schema del endpoint del cache de React Query. Null si no disponible. */
  schema?: { fields: SchemaField[] } | null;
  /** Categorías disponibles (del catálogo). Si no se pasa, usa las por defecto. */
  availableCategories?: string[];
  onConfirm: (overrides: Record<string, unknown>) => void;
  /** Solo en modo "personalizar" */
  onRestore?: () => void;
  onClose: () => void;
}

const REFRESH_OPTIONS = [
  { value: 0,    label: 'Sin auto-refresh'   },
  { value: 30,   label: 'Cada 30 segundos'   },
  { value: 60,   label: 'Cada 1 minuto'      },
  { value: 120,  label: 'Cada 2 minutos'     },
  { value: 300,  label: 'Cada 5 minutos'     },
  { value: 600,  label: 'Cada 10 minutos'    },
  { value: 1800, label: 'Cada 30 minutos'    },
];

export const WidgetCustomizationPanel: React.FC<WidgetCustomizationPanelProps> = ({
  widget,
  preference,
  schema,
  availableCategories,
  onConfirm,
  onRestore,
  onClose,
}) => {
  const { t } = useDashboardTranslation();
  const categories = availableCategories?.length ? availableCategories : DEFAULT_CATEGORIES;
  const mode = preference ? 'personalize' : 'add';

  const [overrides, setOverrides] = useState<Record<string, unknown>>(
    preference?.config_json ?? {}
  );

  // Resolver campos actuales
  const contract = extractChartContract(widget.config_schema);
  const existingFieldMapping = overrides.fieldMapping as FieldMappingOverride | undefined;
  const resolved = resolveChartFields(widget.config_schema, [], existingFieldMapping, widget.field_mapping);

  const activeValuesCount =
    existingFieldMapping?.values?.length ??
    widget.field_mapping?.values?.length ??
    contract?.valueFields.length;
  const compatibleTypes = getCompatibleTypes(widget.tipo, activeValuesCount);
  const showTypeSelector = compatibleTypes.length > 0;
  const currentType = (overrides.chartType as string) ?? widget.tipo;

  // Campos del gráfico: visible si el widget tiene field_mapping configurado O schema en vivo,
  // independiente de si tiene tipos compatibles para cambiar.
  const showFieldMapping = !!(
    widget.field_mapping?.values?.length ||
    widget.field_mapping?.category ||
    schema
  );

  const update = (key: string, value: unknown) =>
    setOverrides(prev => ({ ...prev, [key]: value }));

  const handleFieldMappingChange = (newOverride: FieldMappingOverride | null) => {
    setOverrides(prev => {
      const next = { ...prev };
      if (newOverride) {
        next.fieldMapping = newOverride;
      } else {
        delete next.fieldMapping;
      }
      return next;
    });
  };

  const handleConfirm = () => {
    const clean: Record<string, unknown> = {};

    /**
     * El sello de layout NO es un override del usuario: se conserva SIEMPRE.
     *
     * `config_json` es la bolsa donde este panel guarda los overrides, pero también donde
     * `layoutMigration` estampa `layoutVersion` para no volver a convertir un `row_span` ya
     * convertido. Este panel RECONSTRUYE la bolsa desde cero, así que si el sello no se copia
     * aquí desaparece con cada "Confirmar" — y en la siguiente carga la migración se aplica
     * otra vez sobre un valor ya migrado: 2 → 4 → 7 → 13 filas, sin tope y en silencio.
     */
    if (preference?.config_json?.layoutVersion !== undefined) {
      clean.layoutVersion = preference.config_json.layoutVersion;
    }
    if (overrides.chartType && overrides.chartType !== widget.tipo)                           clean.chartType     = overrides.chartType;
    if (overrides.title && overrides.title !== widget.nombre)                                 clean.title         = overrides.title;
    if (overrides.descripcion && overrides.descripcion !== widget.descripcion)                clean.descripcion   = overrides.descripcion;
    if (overrides.categoria && overrides.categoria !== widget.categoria)                      clean.categoria     = overrides.categoria;
    if (overrides.cols_default && overrides.cols_default !== widget.cols_default)             clean.cols_default  = overrides.cols_default;
    if (overrides.refresh_policy !== undefined && overrides.refresh_policy !== widget.refresh_policy) clean.refresh_policy = overrides.refresh_policy;
    if (overrides.fieldMapping)                                                               clean.fieldMapping  = overrides.fieldMapping;
    onConfirm(clean);
  };

  return (
    <div className="fixed inset-y-0 right-0 w-[348px] bg-white shadow-2xl border-l border-gray-200 z-50 flex flex-col">

      {/* Header */}
      <div className="flex items-center gap-3 p-4 border-b border-gray-100">
        {mode === 'add' && (
          <button onClick={onClose} className="p-1 hover:bg-gray-100 rounded text-gray-500">
            <ArrowLeft className="w-5 h-5" />
          </button>
        )}
        <div className="flex-1 min-w-0">
          <h2 className="text-lg font-semibold text-gray-800">
            {mode === 'add' ? 'Agregar indicador' : 'Personalizar'}
          </h2>
          <p className="text-xs text-gray-500 truncate">{widget.nombre}</p>
        </div>
        <button onClick={onClose} className="p-1 hover:bg-gray-100 rounded text-gray-500">
          <X className="w-5 h-5" />
        </button>
      </div>

      {/* Controles */}
      <div className="flex-1 overflow-y-auto p-4 space-y-5">

        {/* 1. Tipo de gráfico */}
        {showTypeSelector && (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">{t('dashboard.customize.visualization')}</label>
            <div className="grid grid-cols-4 gap-2">
              {([widget.tipo, ...compatibleTypes] as string[]).map(typeId => {
                const cfg = WIDGET_TYPE_MAP[typeId as keyof typeof WIDGET_TYPE_MAP];
                if (!cfg) return null;
                const isAvailable = true;
                const isActive = currentType === typeId;
                return (
                  <button
                    key={typeId}
                    disabled={!isAvailable}
                    onClick={() => update('chartType', typeId)}
                    className={`flex flex-col items-center gap-1 p-2.5 rounded-lg border-2 transition-all text-xs font-medium ${
                      isActive
                        ? 'border-blue-500 bg-blue-50 text-blue-700'
                        : 'border-gray-200 text-gray-600 hover:border-blue-300 hover:bg-blue-50/30'
                    }`}
                  >
                    <cfg.icon className="w-5 h-5" />
                    {cfg.label}
                  </button>
                );
              })}
            </div>
            {currentType === 'chart_donut' && (
              <p className="text-[10px] text-amber-600 mt-1.5 leading-relaxed bg-amber-50 border border-amber-200 rounded px-2 py-1">
                El gráfico circular solo muestra un valor a la vez. Cambia a barras o líneas para comparar varios.
              </p>
            )}
          </div>
        )}

        {/* 2. Campos del gráfico — visible si hay field_mapping o schema disponible */}
        {showFieldMapping && (
          <FieldMappingPanel
            schema={schema ?? null}
            widgetFieldMapping={widget.field_mapping}
            currentCategory={resolved.categoryField}
            currentValues={resolved.valueFields}
            override={existingFieldMapping ?? null}
            chartType={currentType}
            onChange={handleFieldMappingChange}
          />
        )}

        {/* 3. Nombre */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">{t('dashboard.customize.name')}</label>
          <input
            type="text"
            value={(overrides.title as string) ?? widget.nombre}
            onChange={e => update('title', e.target.value)}
            className="w-full p-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 text-sm"
          />
        </div>

        {/* 4. Descripción */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Descripción corta <span className="text-gray-400 font-normal">(opcional)</span>
          </label>
          <input
            type="text"
            value={(overrides.descripcion as string) ?? widget.descripcion}
            onChange={e => update('descripcion', e.target.value)}
            className="w-full p-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 text-sm"
          />
        </div>

        {/* 5. Categoría — solo en modo personalizar */}
        {mode === 'personalize' && (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">{t('dashboard.customize.category')}</label>
            <select
              value={(overrides.categoria as string) ?? widget.categoria}
              onChange={e => update('categoria', e.target.value)}
              className="w-full p-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 bg-white text-sm"
            >
              {categories.map(cat => (
                <option key={cat} value={cat}>{cat}</option>
              ))}
            </select>
          </div>
        )}

        {/* 6. Ancho */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">{t('dashboard.customize.width')}</label>
          <select
            value={(overrides.cols_default as number) ?? widget.cols_default}
            onChange={e => update('cols_default', Number(e.target.value))}
            className="w-full p-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 bg-white text-sm"
          >
            <option value={3}>{t('dashboard.customize.widthSmall')}</option>
            <option value={6}>{t('dashboard.customize.widthMedium')}</option>
            <option value={12}>{t('dashboard.customize.widthFull')}</option>
          </select>
        </div>

        {/* 7. Frecuencia de actualización */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">{t('dashboard.customize.refresh')}</label>
          <select
            value={(overrides.refresh_policy as number) ?? widget.refresh_policy ?? 300}
            onChange={e => update('refresh_policy', Number(e.target.value))}
            className="w-full p-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 bg-white text-sm"
          >
            {REFRESH_OPTIONS.map(opt => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
          </select>
        </div>
      </div>

      {/* Footer */}
      <div className="p-4 border-t border-gray-200 bg-gray-50 space-y-3">
        {mode === 'personalize' ? (
          <div className="flex gap-2">
            <button
              onClick={() => { onRestore?.(); onClose(); }}
              className="flex-1 flex items-center justify-center gap-1.5 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
            >
              <RotateCcw className="w-3.5 h-3.5" /> Restaurar
            </button>
            <button
              onClick={() => { handleConfirm(); onClose(); }}
              className="flex-1 flex items-center justify-center gap-1.5 py-2 text-sm font-semibold text-white bg-blue-600 rounded-lg hover:bg-blue-700"
            >
              <Check className="w-3.5 h-3.5" /> Aplicar
            </button>
          </div>
        ) : (
          <button
            onClick={() => { handleConfirm(); onClose(); }}
            disabled={!(overrides.title as string ?? widget.nombre)?.trim()}
            className="w-full flex items-center justify-center gap-2 py-2.5 bg-blue-600 text-white text-sm font-semibold rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50"
          >
            <Check className="w-4 h-4" /> Agregar al Dashboard
          </button>
        )}
        <p className="text-[11px] text-gray-400 text-center">
          {mode === 'personalize'
            ? 'Los cambios solo afectan tu dashboard. El widget original no se modifica.'
            : 'El indicador se agrega solo a tu dashboard.'}
        </p>
      </div>
    </div>
  );
};

/**
 * Wrapper que inyecta el schema del cache de React Query al panel.
 * Funciona para ambos modos (add y personalize).
 */
export const CustomizationWithSchema: React.FC<
  WidgetCustomizationPanelProps & { period: DashboardPeriod }
> = ({ period, ...props }) => {
  const queryClient = useQueryClient();
  const cached = queryClient.getQueryData<{
    data: unknown;
    schema: { fields: SchemaField[] } | null;
  }>(['widget-data', props.widget.widget_id, period]);
  return <WidgetCustomizationPanel {...props} schema={cached?.schema} />;
};
